import type {
  CanvasGeometry,
  CanvasPoint,
  NodeOverlay,
  PageCanvas,
  PageCanvasBounds,
} from './canvasTypes'
import type { PnidNodeOverlay } from './pnidSchemas'
import { polylineDistance } from './polylineGeometry'

const POLYLINE_HIT_RADIUS_PX = 6

function projectOverlayGeometry(
  geometry: PnidNodeOverlay['data']['geometry'],
  bounds: PageCanvasBounds
): CanvasGeometry {
  return {
    ...geometry,
    points: geometry.points.map((point) => ({
      denormalized_x:
        bounds.denormalized_x + point.normalized_x * bounds.denormalized_width,
      denormalized_y:
        bounds.denormalized_y + point.normalized_y * bounds.denormalized_height,
    })),
  }
}

export function projectNodeOverlays(pages: PageCanvas[]): NodeOverlay[] {
  return pages.flatMap(({ graph, bounds }) => {
    if (bounds.denormalized_width <= 0 || bounds.denormalized_height <= 0)
      return []
    return graph.getNodes().flatMap((node) =>
      node.overlays.map(
        (overlay): NodeOverlay => ({
          overlayId: overlay.id,
          pageId: overlay.data.page_id,
          node,
          geometry: projectOverlayGeometry(overlay.data.geometry, bounds),
        })
      )
    )
  })
}

export function polygonContains(
  points: CanvasPoint[],
  point: CanvasPoint
): boolean {
  let inside = false
  for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
    const a = points[i],
      b = points[j]
    if (
      a.denormalized_y > point.denormalized_y !==
        b.denormalized_y > point.denormalized_y &&
      point.denormalized_x <
        ((b.denormalized_x - a.denormalized_x) *
          (point.denormalized_y - a.denormalized_y)) /
          (b.denormalized_y - a.denormalized_y) +
          a.denormalized_x
    )
      inside = !inside
  }
  return inside
}

function polygonArea(points: CanvasPoint[]): number {
  return (
    Math.abs(
      points.reduce((area, a, i) => {
        const b = points[(i + 1) % points.length]
        return (
          area +
          a.denormalized_x * b.denormalized_y -
          b.denormalized_x * a.denormalized_y
        )
      }, 0)
    ) / 2
  )
}

export function hitTestNodeOverlays(
  overlays: NodeOverlay[],
  point: CanvasPoint,
  screenScale: { scale_x: number; scale_y: number }
): NodeOverlay | undefined {
  const polygons = overlays.filter(
    (overlay) =>
      overlay.geometry.type === 'polygon' &&
      polygonContains(overlay.geometry.points, point)
  )
  polygons.sort(
    (a, b) =>
      polygonArea(a.geometry.points) - polygonArea(b.geometry.points) ||
      a.overlayId.localeCompare(b.overlayId)
  )
  if (polygons[0]) return polygons[0]
  const lines = overlays
    .filter((overlay) => overlay.geometry.type === 'polyline')
    .map((overlay) => ({
      overlay,
      distance: polylineDistance(overlay.geometry.points, point, screenScale),
    }))
    .filter(({ distance }) => distance <= POLYLINE_HIT_RADIUS_PX)
    .sort(
      (a, b) =>
        a.distance - b.distance ||
        a.overlay.overlayId.localeCompare(b.overlay.overlayId)
    )
  return lines[0]?.overlay
}
